import { faShieldCheck } from "@fortawesome/pro-solid-svg-icons/faShieldCheck";
import { faShieldQuartered } from "@fortawesome/pro-solid-svg-icons/faShieldQuartered";
import { faThumbsUp } from "@fortawesome/pro-solid-svg-icons/faThumbsUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, IconButton, Typography, useTheme } from "@mui/material";
import { MprcClickable, MprcLink, MprcRequestErrorReport, MprcText } from "components";
import { UserItemDtoPaginatedList, UserPanelSpecialistInterestDto, UserPanelSpecialistInterestDtoPaginatedList } from "generated/CoreApi_v1";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import { useModalUserProfile } from "providers/UserProfileProvider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ListUsersDrawer } from "views/foundation/user/drawers/ListUsersDrawer";

interface IProps {
    title: string;
    topicId: string;
    viewData: UserPanelSpecialistInterestDtoPaginatedList;
    onChange: (item: UserPanelSpecialistInterestDto, index: number) => void;
}

/**
 * Painel lateral de especialistas e interessados no tópico
 * @param props
 */
export const PanelTopicSpecialistInterest: React.FC<IProps> = (props) => {
    const { topicId, title, viewData, onChange } = props;

    const theme = useTheme();
    const api = useCoreApi();
    const session = useSession();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const { showUser } = useModalUserProfile();

    const [posting, setPosting] = useState<boolean>(false);
    const [dialog, setDialog] = useState<any>();
    const [viewError, setViewError] = useState<any>();

    ///
    const handleAddEndorsement = (item: UserPanelSpecialistInterestDto, index: number) => {
        setPosting(true);
        api.v1.userManagement.addTopicEndorment({ topicId, userId: item.userId })
            .then(result => {
                if (result.data === true) {
                    enqueueSnackbar(t("Feed.Panels.TopicSpecialistInterest.AddEndorsementSuccessMessage"), { variant: "success" });
                    onChange({ ...item, isEndorsed: true, endorsementCount: item.endorsementCount + 1 }, index);
                } else {
                    enqueueSnackbar(t("Feed.Panels.TopicSpecialistInterest.AddEndorsementFailMessage"), { variant: "error" });
                }
            }).catch(e => setViewError(e))
            .finally(() => setPosting(false));
    };

    ///
    const handleRemoveEndorsement = (item: UserPanelSpecialistInterestDto, index: number) => {
        setPosting(true);
        api.v1.userManagement.deleteTopicEndorment({ topicId, userId: item.userId })
            .then(result => {
                if (result.data === true) {
                    enqueueSnackbar(t("Feed.Panels.TopicSpecialistInterest.RemoveEndorsementSuccessMessage"), { variant: "success" });
                    onChange({ ...item, isEndorsed: false, endorsementCount: item.endorsementCount - 1 }, index);
                } else {
                    enqueueSnackbar(t("Feed.Panels.TopicSpecialistInterest.RemoveEndorsementFailMessage"), { variant: "error" });
                }
            }).catch(e => setViewError(e))
            .finally(() => setPosting(false));
    };

    ///
    const showEndorsers = (item: UserPanelSpecialistInterestDto) => {
        setDialog(
            <ListUsersDrawer
                title={t("Feed.Panels.TopicSpecialistInterest.UserEndorsedDrawerTitle")}
                onClose={() => setDialog(null)}
                onGetData={({ searchText, pageNumber, pageSize }, signal) =>
                    new Promise<UserItemDtoPaginatedList>((resolve, reject) => {
                        api.v1.topicManagement.getEndorserUsers({ topicId, userId: item.userId, searchText, pageNumber, pageSize }, { signal })
                            .then(result => resolve(result.data))
                            .catch(e => reject(e));
                    })
                }
            />
        );
    };

    //
    const handleSeeAll = () => {
        setDialog(
            <ListUsersDrawer
                title={t("Feed.Panels.TopicSpecialistInterest.Title")}
                onClose={() => setDialog(null)}
                onGetData={({ searchText, pageNumber, pageSize }, signal) =>
                    new Promise<UserItemDtoPaginatedList>((resolve, reject) => {
                        api.v1.knowledgeFeed.getFeedTopicPanelSpecialistInterest({ topicId, searchText, pageNumber, pageSize }, { signal })
                            .then(result => resolve(result.data))
                            .catch(e => reject(e));
                    })
                }
            />
        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// Render
    ////////////////////////////////////////////////////////////////////////////

    return viewError
        ? <MprcRequestErrorReport error={viewError} />
        : viewData.items.length > 0
            ? (
                <>
                    {dialog}

                    <Typography variant="h3">{title}</Typography>

                    {
                        viewData.items.map((item, index) =>
                            <Grid key={item.userId} container alignItems={item.endorsementCount > 0 ? "baseline" : "center"} spacing={0.6} minHeight={"25px"} marginBottom={0.5}>
                                <Grid>
                                    <MprcClickable cursor="notAllowed" disabled={item.userId == session.user.userId}>
                                        <IconButton
                                            size="small"
                                            disabled={posting || item.userId == session.user.userId}
                                            title={item.isEndorsed
                                                ? t("Feed.Panels.UserProfessionalInterest.RemoveEndorsementButtonTooltip")
                                                : t("Feed.Panels.UserProfessionalInterest.AddEndorsementButtonTooltip")
                                            }
                                            onClick={() =>
                                                item.isEndorsed
                                                    ? handleRemoveEndorsement(item, index)
                                                    : handleAddEndorsement(item, index)
                                            }
                                        >

                                            <FontAwesomeIcon
                                                icon={faThumbsUp}
                                                fixedWidth
                                                color={item.isEndorsed ? theme.palette.highlight.main : theme.palette.muted.light}
                                            />
                                        </IconButton>
                                    </MprcClickable>
                                </Grid>
                                <Grid size="grow" overflow="hidden">
                                    <Typography variant="h6" component="div" noWrap>
                                        <MprcClickable onClick={() => showUser(item.userId)}>{item.name}</MprcClickable>
                                        {
                                            item.isSpecialist
                                                ? <MprcText color="success">
                                                    <span>&nbsp;</span>
                                                    <FontAwesomeIcon title={t("Feed.Panels.TopicSpecialistInterest.IsSpecialistTooltip", { name: item.specialistSealGrantedBy, date: moment(item.specialistSealGrantedOn).format("L") })} icon={faShieldCheck} />
                                                </MprcText>

                                                : item.hasProvenKnowledgeSeal
                                                    ? <MprcText color="info">
                                                        <span>&nbsp;</span>
                                                        <FontAwesomeIcon title={t("Feed.Panels.TopicSpecialistInterest.VerifiedKnowerTooltip")} icon={faShieldQuartered} />
                                                    </MprcText>
                                                    : <></>
                                        }
                                    </Typography>
                                    {
                                        item.endorsementCount > 0 &&
                                        <Typography variant="caption" sx={{ mt: .5, mb: .5 }} component="div">
                                            <MprcLink fontSize="inherit" onClick={() => showEndorsers(item)}>
                                                <b>{t("Feed.Panels.TopicSpecialistInterest.PeopleText", { count: item.endorsementCount })}</b>
                                                &nbsp;
                                                <Box
                                                    component="span"
                                                    color={theme.palette["muted"].main}
                                                >
                                                    {t("Feed.Panels.TopicSpecialistInterest.EndorssedText", { count: item.endorsementCount })}
                                                </Box>
                                            </MprcLink>
                                        </Typography>
                                    }
                                </Grid>
                            </Grid>
                        )
                    }
                    {
                        viewData.totalRows > 10 &&
                        <Box marginTop={1}>
                            <Typography
                                variant="linkActionHeader"
                                component="a"
                                onClick={() => handleSeeAll()}>
                                <b>{t("Feed.Panels.TopicSpecialistInterest.SeeAllText")}</b>
                            </Typography>
                        </Box>
                    }
                </>
            )
            : null;
};
